/**
 * ConceptGraphStats — health of the concept/problem-type substrate, per subject.
 *
 * The substrate is built offline (Backend/functions/scripts/areas/problem-types) and had no
 * in-app reporting, so questions like "how much of the registry is actually used?" or
 * "are the tags lumped onto a few concepts?" needed ad-hoc Cosmos queries. Every figure
 * here is computed live by `GET /concept-graph-stats` — there is no cached snapshot, so
 * Refresh genuinely re-measures.
 *
 * What each number means, and how to read it without being misled by an unevenly loaded
 * question bank: docs/FeatureDesigns/modules-curriculum-topics-concepts/concept-graph-current.md §3.
 */
import { useQuery } from '@tanstack/react-query';
import { conceptsApi } from '../api/conceptsApi';
import type { ConceptGraphSubjectStats } from '../types';
import { Button, InfoTip } from '@shared-ui/components';

const CANONICAL_BANDS = ['easy', 'medium', 'hard'];

const pct = (n: number, d: number): string => (d ? `${((100 * n) / d).toFixed(0)}%` : '—');

const card: React.CSSProperties = {
  border: '1px solid var(--border-color)', borderRadius: 8, padding: 14,
  background: 'var(--surface)', minWidth: 0,
};
const label: React.CSSProperties = { fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.4 };
const big: React.CSSProperties = { fontSize: 22, fontWeight: 700, lineHeight: 1.2 };
const sub: React.CSSProperties = { fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 };
const row: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 };

/** A number is only meaningful next to what it is out of. */
function Stat({ title, value, detail, warn, tip }: {
  title: string; value: string | number; detail?: string; warn?: boolean; tip?: string;
}) {
  return (
    <div style={card}>
      <div style={label}>
        {title}{tip ? <InfoTip text={tip} /> : null}
      </div>
      <div style={{ ...big, color: warn ? '#b91c1c' : 'inherit' }}>{value}</div>
      {detail ? <div style={sub}>{detail}</div> : null}
    </div>
  );
}

function SubjectPanel({ s }: { s: ConceptGraphSubjectStats }) {
  const drift = Object.keys(s.problemTypes.bands).filter(b => !CANONICAL_BANDS.includes(b));
  const singletonPct = s.problemTypes.total ? (100 * s.problemTypes.singletons) / s.problemTypes.total : 0;
  const unservablePct = s.unservablePrerequisites.links
    ? (100 * s.unservablePrerequisites.broken) / s.unservablePrerequisites.links : 0;

  return (
    <section style={{ marginBottom: 28 }}>
      <h3 style={{ margin: '0 0 10px', textTransform: 'capitalize' }}>
        {s.subject}
        <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 10 }}>
          {s.questions.jeeTotal.toLocaleString()} JEE questions
        </span>
      </h3>

      <div style={row}>
        <Stat
          title="Tag coverage" value={`${s.questions.coveragePct}%`}
          detail={`${s.questions.tagged.toLocaleString()} of ${s.questions.jeeTotal.toLocaleString()} tagged`}
          tip="Share of the bank carrying a primaryConceptId. Says nothing about whether the tag is CORRECT — that needs a gold set."
        />
        <Stat
          title="Concepts anchored" value={`${s.concepts.anchored} / ${s.concepts.fine}`}
          detail={`${s.concepts.orphans} orphans (${pct(s.concepts.orphans, s.concepts.fine)})`}
          tip="Fine concepts that have at least one problem type. An orphan means we hold no questions for it — either a content gap, or tagging parked them on a neighbour."
        />
        <Stat
          title="Problem types" value={s.problemTypes.total.toLocaleString()}
          detail={`${s.questions.compression}× compression · ${s.problemTypes.typesPerGroup} per concept×band group`}
          tip="Equivalence classes of interchangeable questions. Compression is questions per type."
        />
        <Stat
          title="Singleton types" value={`${s.problemTypes.singletons}`}
          detail={`${singletonPct.toFixed(0)}% of types`}
          warn={singletonPct >= 50}
          tip="Types holding one question. They have no merge sibling, so 'another like this' cannot fire. Unavoidable in thin chapters; over-splitting elsewhere."
        />
        <Stat
          title="Lumping" value={s.problemTypes.anchorsOverTwenty}
          detail={`concepts ≥20q · biggest holds ${s.problemTypes.largestAnchor}`}
          warn={s.problemTypes.largestAnchor >= 60}
          tip="Concepts crowded with questions, usually because tagging chose an umbrella concept over its specific siblings. Fixed by retag_siblings.cjs."
        />
        <Stat
          title="Relations" value={s.relations.total.toLocaleString()}
          detail={Object.entries(s.relations.byType).map(([k, v]) => `${k.replace('-variant', '')} ${v}`).join(' · ')}
          tip="Typed edges between problem types that serving traverses by intent."
        />
        <Stat
          title="Unreviewed directed edges" value={s.relations.unreviewedDirected.toLocaleString()}
          warn={s.relations.unreviewedDirected > 0}
          detail="harder/easier + prereq/follow-up still reviewStatus:auto"
          tip="Direction is derived deterministically and only sampled by an LLM. Human review is the gate before prod — a reversed edge sends a student the wrong way."
        />
        <Stat
          title="Unservable prerequisites" value={`${s.unservablePrerequisites.broken} / ${s.unservablePrerequisites.links}`}
          detail={`${unservablePct.toFixed(0)}% dead-end`}
          warn={unservablePct >= 20}
          tip="Prerequisite links pointing at a concept with no questions, so remediation has nothing to serve."
        />
        <Stat
          title="Embeddings" value={s.embeddings.toLocaleString()}
          detail="concept-abstracted, 3072-dim"
          tip="One vector per question, used only to augment similar-concept edges."
        />
        <Stat
          title="Difficulty bands"
          value={drift.length ? `${drift.length} off-enum` : Object.keys(s.problemTypes.bands).length}
          warn={drift.length > 0}
          detail={drift.length
            ? `drift: ${drift.join(', ')}`
            : Object.entries(s.questions.byDifficulty).map(([k, v]) => `${k} ${v}`).join(' · ')}
          tip="Bands must be exactly easy/medium/hard. Anything else means a producer wrote past the enum, which splits a concept's types and mis-orders harder/easier edges."
        />
      </div>
    </section>
  );
}

export function ConceptGraphStats({ subject }: { subject?: string }) {
  const { data, isFetching, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['conceptGraphStats', subject ?? 'all'],
    queryFn: () => conceptsApi.getConceptGraphStats(subject),
    staleTime: 0,            // always considered stale: Refresh must re-measure, never replay
    refetchOnWindowFocus: false,
  });

  return (
    <div style={{ padding: '4px 2px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Measuring…' : 'Refresh'}
        </Button>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {dataUpdatedAt ? `Measured ${new Date(dataUpdatedAt).toLocaleTimeString()}` : 'Computed live on each refresh'}
        </span>
      </div>

      {error ? (
        <div style={{ color: '#b91c1c', fontSize: 13 }}>{(error as Error).message}</div>
      ) : !data ? (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Measuring…</div>
      ) : data.subjects.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No concept registry for this subject yet.</div>
      ) : (
        data.subjects.map(s => <SubjectPanel key={s.subject} s={s} />)
      )}

      <p style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 760, marginTop: 4 }}>
        The question bank is loaded unevenly by design, so counts are not comparable across
        subjects or chapters — a low figure may mean "not loaded yet" rather than "not covered".
        Read coverage and lumping together, within a subject. Full notes in
        <code style={{ margin: '0 4px' }}>docs/FeatureDesigns/modules-curriculum-topics-concepts/concept-graph-current.md</code>.
      </p>
    </div>
  );
}
